/**
 * @file        src/pages/platform/TenantGatewaysPage.jsx
 * @description Tenant Management → Payment Gateways. The selected tenant's
 *              `payment_gateways` rows (in the TENANT's own DB — the merchant
 *              key + salt here sign that tenant's checkout). Moved out of the
 *              tenant edit page's stacked panels into its own sidebar entry
 *              (?tenant=<id> picks the tenant). One ACTIVE gateway per brand;
 *              key/salt are write-only. Mandatory before go-live: every active
 *              brand needs an active credentialed gateway or checkout fails
 *              closed (503 GATEWAY_NOT_CONFIGURED).
 * @author      Kinetika Labs
 * @created     2026-08-10
 * @changelog
 *   - 2026-09-16 Claude — Password fields carry autoComplete="new-password" +
 *                          data-lpignore / data-1p-ignore, so the browser and
 *                          password managers stop pre-filling the signed-in
 *                          admin's own password into them. Only Login.jsx keeps
 *                          "current-password".
 *   - 2026-08-10 Claude — Initial (extracted from AddTenant.jsx unchanged, plus
 *                          the tenant picker wrapper).
 *   - 2026-09-17 Claude — Merchant key + salt are now <PasswordInput>.
 *                          A mistyped salt signs a real checkout wrongly and
 *                          neither value is ever readable again once saved.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Chip from '../../components/Chip';
import Select from '../../components/Select';
import { get, post, patch } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { toast } from '../../lib/toast';
import TenantPicker, { useTenantParam } from './TenantPicker';
import Field from './Field';
import { useTenantBrands } from './TenantBrandsPage';
import PasswordInput from '../../components/PasswordInput';

// Which payment provider processes this tenant's checkout. Mirrors
// GATEWAY_PROVIDERS in admin_backend/src/routes/tenants.routes.js — only
// providers with a working PER-TENANT adapter belong here, because selecting one
// promises that a tenant set to it can actually take a payment. Easebuzz only
// for now: booking_backend's Razorpay/PayPhi services still read process.env.
const GW_PROVIDERS = [{ value: 'easebuzz', label: 'Easebuzz' }];
const GW_ENVS = [{ value: 'test', label: 'Test' }, { value: 'prod', label: 'Production' }];
const STATUS = [{ value: 'active', label: 'Active' }, { value: 'suspended', label: 'Suspended' }];
// brand starts EMPTY — the options come from the tenant's Brands registry and
// there is no default brand to assume.
const emptyGateway = {
  brand: '', name: 'easebuzz', display_name: '', gateway_id: '',
  convenience_fee: 0, is_active: true, merchant_key: '', salt: '', gateway_env: 'test',
};

export default function TenantGatewaysPage() {
  const [tenantId, setTenantId] = useTenantParam();
  return (
    <>
      <PageHead title="Payment Gateways" subtitle="Platform · Tenant Management · per-tenant merchant credentials" />
      <article className="panel glass" style={{ marginBottom: 16 }}>
        <TenantPicker value={tenantId} onChange={setTenantId} />
      </article>
      {tenantId ? <PaymentGateways tenantId={tenantId} /> : (
        <article className="panel glass"><div className="empty">Select a tenant to manage its payment gateways.</div></article>
      )}
    </>
  );
}

function PaymentGateways({ tenantId }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null); // row being edited, or 'new'
  const { data: brandRows } = useTenantBrands(tenantId);
  const brands = (brandRows || []).map((b) => ({ value: b.code, label: b.name }));

  const { data, isLoading, isError } = useQuery({
    queryKey: qk.tenants.paymentGateways(tenantId),
    queryFn: () => get(`/tenants/${tenantId}/payment-gateways`).then((r) => r.data),
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: qk.tenants.paymentGateways(tenantId) });
    qc.invalidateQueries({ queryKey: qk.tenants.readiness(tenantId) });
    setEditing(null);
  };

  const rows = data || [];

  return (
    <article className="panel glass">
      <div className="panel-head">
        <div>
          <h2>Payment Gateways</h2>
          <p className="muted">
            Stored in this tenant's own database. The Merchant Key and Salt here sign that
            tenant's checkout — the booking backend no longer falls back to the shared
            environment credentials, so a brand with no configured gateway cannot take payment.
          </p>
        </div>
        {editing === null ? (
          <button className="btn primary" onClick={() => setEditing('new')}>
            <Icon name="plus" /> Add Gateway
          </button>
        ) : null}
      </div>

      {isLoading ? (
        <div className="empty">Loading gateways…</div>
      ) : isError ? (
        <div className="empty"><p className="field-error">Could not load this tenant's gateways.</p></div>
      ) : (
        <>
          {rows.length === 0 && editing === null ? (
            <div className="empty">
              <p className="field-error">No gateway configured — checkout will fail for this tenant.</p>
            </div>
          ) : null}

          {rows.length ? (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr><th>Brand</th><th>Provider</th><th>Gateway</th><th>Env</th><th>Credentials</th><th>Status</th><th>Action</th></tr>
                </thead>
                <tbody>
                  {rows.map((g) => (
                    <tr key={g.id}>
                      <td><b>{brands.find((b) => b.value === g.brand)?.label || g.brand}</b></td>
                      <td>{GW_PROVIDERS.find((p) => p.value === g.name)?.label || g.name}</td>
                      <td>{g.display_name || g.name}</td>
                      <td>
                        {g.gateway_env === 'prod' ? <Chip tone="warn">Production</Chip>
                          : g.gateway_env === 'test' ? <Chip tone="pend">Test</Chip>
                          : <Chip tone="rej">Not set</Chip>}
                      </td>
                      {/* The API never returns the key itself — only this boolean. */}
                      <td>{g.has_custom_credentials ? <Chip tone="ok">Set</Chip> : <Chip tone="rej">Missing</Chip>}</td>
                      <td>{g.is_active ? <Chip tone="ok">Active</Chip> : <Chip tone="rej">Inactive</Chip>}</td>
                      <td className="row-act">
                        <button className="ib edit" aria-label="Edit" onClick={() => setEditing(g)}>
                          <Icon name="edit" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {editing !== null ? (
            <GatewayForm
              tenantId={tenantId}
              gateway={editing === 'new' ? null : editing}
              brands={brands}
              onSaved={refresh}
              onCancel={() => setEditing(null)}
            />
          ) : null}
        </>
      )}
    </article>
  );
}

function GatewayForm({ tenantId, gateway, brands = [], onSaved, onCancel }) {
  const isNew = !gateway;
  // Key/salt are write-only — the list never returns them, so the inputs start
  // blank and a blank submit on an existing row means "keep the stored value".
  const [form, setForm] = useState(
    isNew ? emptyGateway : {
      ...emptyGateway,
      ...gateway,
      merchant_key: '', salt: '',
      // Pre-fill from the STORED value. Defaulting to 'test' here would resubmit
      // 'test' for a live gateway and silently demote it out of production.
      gateway_env: gateway.gateway_env || '',
      display_name: gateway.display_name || '',
      gateway_id: gateway.gateway_id || '',
      convenience_fee: gateway.convenience_fee ?? 0,
    },
  );
  const [errors, setErrors] = useState({});

  const mut = useMutation({
    mutationFn: (body) => (isNew
      ? post(`/tenants/${tenantId}/payment-gateways`, body)
      : patch(`/tenants/${tenantId}/payment-gateways/${gateway.id}`, body)),
    onSuccess: () => {
      toast.ok('Gateway saved', `${form.display_name || form.name} (${form.brand}) saved.`);
      onSaved();
    },
    onError: (err) => {
      const fields = err.response?.data?.error?.fields;
      if (fields) setErrors(fields);
      toast.err('Could not save', err.response?.data?.error?.message || 'Validation failed');
    },
  });

  function submit(e) {
    e.preventDefault();
    const fe = {};
    // No default brand: the operator must say which storefront's merchant
    // account this row signs for.
    if (!form.brand) fe.brand = 'Pick a brand';
    if (!form.name) fe.name = 'Required';
    // Written to payments.payment_gateway on every transaction for this tenant,
    // so a blank would leave the processor unnamed on invoices and reports.
    if (!form.display_name.trim()) fe.display_name = 'Required';
    // Mandatory on create only: gatewayConfigService fails closed, so a new row
    // without credentials would be a gateway that cannot take a payment.
    if (isNew && !form.merchant_key.trim()) fe.merchant_key = 'Required';
    if (isNew && !form.salt.trim()) fe.salt = 'Required';
    // An older row may have no env stored; make the operator choose rather than
    // letting a blank through (the API rejects it) or defaulting it to 'test'.
    if (!form.gateway_env) fe.gateway_env = 'Choose Test or Production';
    if (Object.keys(fe).length) return setErrors(fe);
    setErrors({});

    const body = {
      brand: form.brand,
      name: form.name.trim(),
      display_name: form.display_name.trim(),
      gateway_id: form.gateway_id.trim(),
      convenience_fee: Number(form.convenience_fee) || 0,
      is_active: Boolean(form.is_active),
      gateway_env: form.gateway_env,
    };
    if (form.merchant_key.trim()) body.merchant_key = form.merchant_key.trim();
    if (form.salt.trim()) body.salt = form.salt.trim();
    mut.mutate(body);
  }

  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <form className="form-grid" onSubmit={submit} style={{ marginTop: 12 }}>
      <div className="field full">
        <label className="section-label">{isNew ? 'New gateway' : `Editing: ${gateway.display_name || gateway.name}`}</label>
      </div>

      <Field label="Brand" required err={errors.brand}
             hint={brands.length ? undefined : 'Register a brand under Tenant Management → Brands first.'}>
        <Select value={form.brand} onChange={(v) => setForm({ ...form, brand: v })} options={brands}
                placeholder="Select brand" invalid={Boolean(errors.brand)} />
      </Field>
      {/* A row saved before this was a dropdown may hold a provider that is not
          in the list. Surface it as its own option rather than letting the
          Select fall back to the first entry — that would silently switch the
          tenant's provider on save. Submitting it fails the server enum, which
          is correct: it forces an explicit choice. */}
      <Field label="Payment Provider" required err={errors.name}
             hint={GW_PROVIDERS.some((p) => p.value === form.name)
               ? "Which integration processes this tenant's checkout. Only providers with a per-tenant adapter are listed."
               : `"${form.name}" has no per-tenant adapter — pick a supported provider.`}>
        <Select
          value={form.name}
          onChange={(v) => setForm({ ...form, name: v })}
          options={GW_PROVIDERS.some((p) => p.value === form.name)
            ? GW_PROVIDERS
            : [{ value: form.name, label: `${form.name || '—'} (unsupported)` }, ...GW_PROVIDERS]}
        />
      </Field>
      <Field label="Display Name" required err={errors.display_name}
             hint="Recorded on every payment for this tenant and shown on invoices and reconciliation reports.">
        <input value={form.display_name} onChange={on('display_name')} placeholder="e.g. Easebuzz" />
      </Field>
      <Field label="Gateway ID" err={errors.gateway_id}>
        <input value={form.gateway_id} onChange={on('gateway_id')} placeholder="Merchant id / account email" />
      </Field>
      <Field label="Convenience Fee (%)" err={errors.convenience_fee}>
        <input type="number" step="0.01" min="0" max="100" value={form.convenience_fee} onChange={on('convenience_fee')} />
      </Field>
      <Field label="Status">
        <Select
          value={form.is_active ? 'active' : 'inactive'}
          onChange={(v) => setForm({ ...form, is_active: v === 'active' })}
          options={STATUS}
        />
      </Field>

      <Field label="Merchant Key" required={isNew} err={errors.merchant_key}
             hint={isNew ? undefined : 'Leave blank to keep the stored key.'}>
        <PasswordInput value={form.merchant_key} onChange={on('merchant_key')}
                       placeholder={isNew ? 'EASEBUZZ_MERCHANT_KEY for this tenant' : '••••••••'} autoComplete="new-password" data-lpignore="true" data-1p-ignore />
      </Field>
      <Field label="Salt" required={isNew} err={errors.salt}
             hint={isNew ? undefined : 'Leave blank to keep the stored salt.'}>
        <PasswordInput value={form.salt} onChange={on('salt')}
                       placeholder={isNew ? 'EASEBUZZ_SALT for this tenant' : '••••••••'} autoComplete="new-password" data-lpignore="true" data-1p-ignore />
      </Field>
      <Field label="Gateway Environment" required err={errors.gateway_env}
             hint={form.gateway_env === 'prod' ? 'Live — this gateway takes real money.' : undefined}>
        <Select
          value={form.gateway_env}
          onChange={(v) => setForm({ ...form, gateway_env: v })}
          options={form.gateway_env ? GW_ENVS : [{ value: '', label: 'Select…' }, ...GW_ENVS]}
        />
      </Field>

      <div className="form-actions">
        <button className="btn primary" type="submit" disabled={mut.isPending}>
          <Icon name="check" /> {mut.isPending ? 'Saving…' : 'Save Gateway'}
        </button>
        <button className="btn ghost" type="button" onClick={onCancel}><Icon name="x" /> Cancel</button>
      </div>
    </form>
  );
}
